import * as React from 'react'
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { billingService } from '@/services/BillingService'
import { organizationKeys } from '@/hooks/queryKeys'

/** Send the browser to a Stripe-hosted page. The page itself is left behind. */
function goTo(url: string): void {
  window.location.assign(url)
}

/** Start Checkout and go there. */
export function useStartCheckout(): UseMutationResult<void, Error, void> {
  return useMutation({ mutationFn: async () => goTo((await billingService.checkout()).url) })
}

/** Open Stripe's billing page and go there. */
export function useOpenBillingPortal(): UseMutationResult<void, Error, void> {
  return useMutation({ mutationFn: async () => goTo((await billingService.portal()).url) })
}

/** Cancel or undo a cancellation, then show the organization as it now stands. */
export function useCancelSubscription(): UseMutationResult<void, Error, 'cancel' | 'resume'> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (action: 'cancel' | 'resume') => (action === 'cancel' ? billingService.cancel() : billingService.resume()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: organizationKeys.all })
    },
  })
}

/**
 * Finish a Checkout the owner has just come back from.
 *
 * Stripe returns them with `?checkout=<session id>`. The subscription is linked
 * straight away rather than when the webhook lands, so the page they arrive on
 * already shows it — and the parameter is removed so a reload does not do it
 * twice. Runs wherever the owner lands: the billing page, or the lock screen
 * that stands in for it while the company is locked.
 */
export function useCheckoutReturn(): { finishing: boolean; error: string | null } {
  const [params, setParams] = useSearchParams()
  const queryClient = useQueryClient()
  const sessionId = params.get('checkout')
  const [state, setState] = React.useState<{ finishing: boolean; error: string | null }>({ finishing: false, error: null })
  const started = React.useRef<string | null>(null)

  React.useEffect(() => {
    if (!sessionId || started.current === sessionId) return
    started.current = sessionId
    setState({ finishing: true, error: null })
    billingService
      .completeCheckout(sessionId)
      .then(() => {
        void queryClient.invalidateQueries({ queryKey: organizationKeys.all })
        setState({ finishing: false, error: null })
      })
      .catch((error: Error) => setState({ finishing: false, error: error.message }))
      .finally(() => {
        const next = new URLSearchParams(params)
        next.delete('checkout')
        setParams(next, { replace: true })
      })
  }, [sessionId, params, setParams, queryClient])

  return state
}
